<template>
  <div class="ms-detail ms-detail--wide">
    <v-btn variant="text" size="small" prepend-icon="mdi-chevron-left" color="medium-emphasis" class="mb-4" @click="$emit('back')">
      All automations
    </v-btn>

    <!-- title row -->
    <div class="d-flex align-start ga-4 mb-2">
      <v-avatar color="primary" variant="tonal" rounded="lg" size="50" class="mt-1">
        <v-icon :icon="meta.icon" />
      </v-avatar>
      <div class="flex-grow-1">
        <v-text-field
          :model-value="model.name"
          variant="plain"
          density="compact"
          hide-details
          class="text-h4 font-weight-bold"
          @update:model-value="(v) => patchText({ name: v })"
          @blur="flushTextSave"
        />
        <div class="d-flex align-center ga-2 text-caption text-medium-emphasis mt-1">
          <span class="text-uppercase">{{ meta.label }}</span>
          <span>·</span>
          <span class="text-primary">{{ whenLabel }}</span>
        </div>
      </div>

      <v-chip
        v-if="model.type !== 'schedule'"
        :color="model.status === 'active' ? 'success' : 'grey'"
        variant="tonal"
        class="mr-2"
        @click="toggleStatus"
      >
        <v-icon start :icon="model.status === 'active' ? 'mdi-circle' : 'mdi-circle-outline'" size="x-small" />
        {{ model.status === 'active' ? 'Active' : 'Paused' }}
      </v-chip>
      <!-- Schedules: explicit enabled/disabled switch (persisted by Save — maps to the record's
           enabled/disabled status, same as the Advanced card's toggle). -->
      <v-switch
        v-if="model.type === 'schedule'"
        :model-value="model.status === 'active'"
        color="primary"
        density="compact"
        hide-details
        class="flex-grow-0 mr-4"
        :label="model.status === 'active' ? 'Enabled' : 'Disabled'"
        @update:model-value="(v) => patchDiscrete({ status: v ? 'active' : 'paused' })"
      />
      <v-btn
        v-if="model.type !== 'schedule'"
        color="primary"
        variant="tonal"
        prepend-icon="mdi-play"
        :loading="running"
        class="mr-2"
        @click="onRunClick"
      >
        Run
      </v-btn>
      <!-- Schedules keep an explicit Save, mirroring the Advanced ScheduledRunCard (the one Advanced
           editor with a Save button). Workflows/factories autosave like their Advanced editors. -->
      <v-btn v-if="model.type === 'schedule'" color="primary" prepend-icon="mdi-content-save" @click="$emit('save', model)">
        Save
      </v-btn>
    </div>

    <p v-if="model.type !== 'schedule'" class="text-caption text-medium-emphasis mb-4">
      Changes here edit the real {{ model.type }} and save automatically. Run starts it;
      progress and past runs live in the Runs tab.
    </p>

    <!-- set up / runs — the same two-surface split the Advanced editors use -->
    <v-tabs
      v-if="model.type !== 'schedule'"
      v-model="view"
      color="primary"
      density="comfortable"
      class="mb-6"
    >
      <v-tab value="setup" prepend-icon="mdi-pencil-outline">Set up</v-tab>
      <v-tab value="runs" prepend-icon="mdi-play-circle-outline">Runs</v-tab>
    </v-tabs>

    <v-window v-model="view" :touch="false">
      <v-window-item value="setup">

    <!-- 1 · what it does -->
    <EditorBlock n="1" title="What it does" hint="Tell it the goal in plain language. This is the instruction the lead assistant follows.">
      <div v-if="model.type === 'workflow' && graph" class="d-flex justify-end mb-3">
        <v-btn-toggle :model-value="goalView" mandatory density="compact" variant="outlined" @update:model-value="goalView = $event">
          <v-btn value="text" prepend-icon="mdi-text-box-outline">Text</v-btn>
          <v-btn value="visual" prepend-icon="mdi-graph">Visual</v-btn>
        </v-btn-toggle>
      </div>

      <WorkflowGraphView
        v-if="goalView === 'visual' && model.type === 'workflow' && graph"
        :nodes="graph.nodes"
        :edges="graph.edges"
      />
      <v-textarea
        v-else
        :model-value="model.job"
        variant="outlined"
        rows="3"
        auto-grow
        hide-details
        @update:model-value="(v) => patchText({ job: v })"
        @blur="flushTextSave"
      />
    </EditorBlock>

    <!-- 2 · inputs -->
    <EditorBlock v-if="model.type !== 'schedule'" n="2" title="What it works with" :hint="inputsHint">
      <!-- Workflows: reuse the Advanced visual/JSON payload tree so both surfaces edit data the same way. -->
      <WorkflowPayloadEditor
        v-if="model.type === 'workflow'"
        :model-value="model.dataPayload || '{}'"
        @update:model-value="onPayload"
      />
      <!-- Factories: a free-text initial carry payload (default 'execute'), handed straight to the run
           on Run. Mirrors the Advanced "Initial Payload" field; session-only via patch(), no backend home. -->
      <v-textarea
        v-else
        :model-value="model.carryPayload ?? 'execute'"
        variant="outlined"
        density="compact"
        rows="2"
        auto-grow
        hide-details
        @update:model-value="(v) => patch({ carryPayload: v })"
      />
    </EditorBlock>

    <!-- 3 · how it works -->
    <EditorBlock :n="model.type === 'schedule' ? '2' : '3'" title="How it works" :hint="howHint">
      <WorkflowFlow
        v-if="model.type === 'workflow'"
        :helpers="model.helpers"
        :lead="model.lead"
        :agents="agents"
        :inputs="model.inputs"
        @update:helpers="(v) => patchDiscrete({ helpers: v })"
        @update:lead="(v) => patchDiscrete({ lead: v })"
        @edit-helper="onHelperIntent"
        @remove-helper="onHelperIntent"
        @add-helper="$emit('add-helper', model)"
      />
      <FactorySteps
        v-else-if="model.type === 'factory'"
        :steps="model.steps"
        :agents="agents"
        :workflows="workflows"
        @update:steps="(v) => patchDiscrete({ steps: v })"
      />
      <div v-else class="d-flex align-center flex-wrap ga-2 text-body-1 text-medium-emphasis">
        <ChipSelect :model-value="model.lead" :options="agentOptions(model.lead)" icon="mdi-robot" accent @update:model-value="(v) => patchDiscrete({ lead: v })" />
        <span>runs the job and emails you the result.</span>
      </div>

      <!-- Concurrency — the same control the Advanced WorkflowEditor uses (shared component,
           same 1–10 range), with plain-language copy. -->
      <ConcurrencySlider
        v-if="model.type === 'workflow'"
        label="Helpers working at once"
        tooltip="How many helpers can work at the same time."
        :model-value="model.concurrency ?? 1"
        class="mt-4"
        @update:modelValue="(v) => patchDiscrete({ concurrency: v })"
      />
    </EditorBlock>

    <!-- 4 · when. Hidden for workflows: they can't be scheduled directly, so their WhenEditor would
         collapse to a single "When I ask" button — pure noise. -->
    <EditorBlock v-if="model.type !== 'workflow'" :n="model.type === 'schedule' ? '3' : '4'" title="When it runs">
      <WhenEditor :type="model.type" :when="model.when" @update:when="(v) => patchDiscrete({ when: v })" />
    </EditorBlock>

    <!-- delete — its own section at the end of the editor, away from the everyday controls -->
    <EditorBlock title="Delete" :hint="`Removes this ${meta.label.toLowerCase()} for good — it disappears from Advanced too.`">
      <v-btn color="error" variant="tonal" prepend-icon="mdi-trash-can-outline" @click="confirmDelete = true">
        Delete this {{ meta.label.toLowerCase() }}
      </v-btn>
    </EditorBlock>

      </v-window-item>

      <!-- runs — the exact Advanced run browsers, fed by the same streaming state (owned by the view) -->
      <v-window-item value="runs">
        <EditorBlock v-if="model.type !== 'schedule' && runState" title="Runs" hint="Live progress and past runs — the same view as Advanced.">
          <WorkflowRunBrowser
            v-if="model.type === 'workflow'"
            :workflow-id="model.id"
            :workflow-name="model.name"
            :live-run-state="runState"
            @cancel="$emit('cancel-run', model)"
          />
          <FactoryRunBrowser
            v-else
            :factory-id="model.id"
            :factory-name="model.name"
            :live-run-state="runState"
            @pause="$emit('pause-run', model)"
            @resume="$emit('resume-run', model)"
            @cancel="$emit('cancel-run', model)"
          />
        </EditorBlock>
      </v-window-item>
    </v-window>

    <ConfirmationDialog
      v-model="confirmDelete"
      title="Delete automation?"
      :message="`This deletes the real ${model.type} — it disappears from Advanced too.`"
      :item-name="model.name"
      :item-type="meta.label"
      @confirm="onDeleteConfirm"
      @cancel="confirmDelete = false"
    />
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import { debounce, DebouncedFunc } from 'lodash'
import EditorBlock from './EditorBlock.vue'
import ChipSelect from './ChipSelect.vue'
import WorkflowFlow from './WorkflowFlow.vue'
import FactorySteps from './FactorySteps.vue'
import WhenEditor from './WhenEditor.vue'
import { defineAsyncComponent } from 'vue'
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue'
import WorkflowPayloadEditor from '@/components/workflows/WorkflowPayloadEditor.vue'
import ConcurrencySlider from '@/components/workflows/ConcurrencySlider.vue'
import WorkflowGraphView from './WorkflowGraphView.vue'
import { TYPE_META, whenLine } from '@/data/automations'
import { inputsFromPayload } from '@/utils/automations'
import type { HelperTokenEdit } from '@/utils/workflowHelpers'
import type { Automation, ChipOption } from '@/types/automations'
import type { Node, Edge } from '@vue-flow/core'
import type { WorkflowStreamState } from '@/composables/useWorkflowStream'
import type { FactoryStreamState } from '@/composables/useFactoryStream'

@Options({
  components: {
    EditorBlock,
    ChipSelect,
    WorkflowFlow,
    FactorySteps,
    WhenEditor,
    ConfirmationDialog,
    WorkflowPayloadEditor,
    ConcurrencySlider,
    WorkflowGraphView,
    // The run browsers render inside the lazily-activated Runs tab — load them as async chunks so
    // their component trees stay out of the main dashboard bundle.
    WorkflowRunBrowser: defineAsyncComponent(() => import('@/components/workflows/WorkflowRunBrowser.vue')),
    FactoryRunBrowser: defineAsyncComponent(() => import('@/components/factories/FactoryRunBrowser.vue'))
  },
  props: {
    automation: { type: Object, required: true },
    agents: { type: Array, default: () => [] },
    // The user's workflows ({ id, name }) factory steps can run — passed through to FactorySteps.
    workflows: { type: Array, default: () => [] },
    graph: { type: Object, default: null }, // { nodes, edges } for workflows; drives the Visual goal view
    runState: { type: Object, default: null }, // live stream state for this automation's type (owned by the view)
    running: { type: Boolean, default: false }
  },
  emits: ['update:automation', 'back', 'add-helper', 'save', 'run', 'delete', 'cancel-run', 'pause-run', 'resume-run'],
  watch: {
    // VCC field initializers run before props are wired, so a `{ ...this.automation }` copy can start
    // empty (model.type undefined -> TYPE_META lookup undefined -> render crash). Sync from the prop
    // with an immediate watcher, which fires in `created` once the prop is available.
    automation: {
      immediate: true,
      handler(this: AutomationDetail, value: Automation) {
        if (!value) return
        // Switching to a different automation without unmounting (openId change while the detail
        // stays rendered): persist any pending autosave of the outgoing one before the model swaps.
        if (this.model?.id && value.id !== this.model.id) this.flushPendingSaves()
        this.model = { ...value }
      }
    },
    // Mirror the Advanced editors: jump to the Runs tab when a live run is attached (e.g. an
    // in-flight run resumed on open).
    liveStatus(this: AutomationDetail, status: string) {
      if (status === 'queued' || status === 'running') this.view = 'runs'
    }
  }
})
export default class AutomationDetail extends Vue {
  automation!: Automation
  agents!: string[]
  workflows!: Array<{ id: string; name: string }>
  graph!: { nodes: Node[]; edges: Edge[] } | null
  runState!: WorkflowStreamState | FactoryStreamState | null
  running!: boolean

  model: Automation = { ...this.automation }
  goalView: 'text' | 'visual' = 'text'
  view: 'setup' | 'runs' = 'setup'
  confirmDelete = false

  // Autosave, mimicking the Advanced editors (WorkflowEditor/FactoryEditor — no Save button there
  // either): discrete edits persist after 500ms, free-text after 5s of quiet or immediately on blur.
  // Assigned in created() — vue-class-component field initializers run before methods are bindable.
  private queueSave!: DebouncedFunc<() => void>
  private queueTextSave!: DebouncedFunc<() => void>

  created() {
    this.queueSave = debounce(() => this.emitSave(), 500)
    this.queueTextSave = debounce(() => this.emitSave(), 5000)
  }

  beforeUnmount() {
    // Matches the Advanced editors: pending edits persist on leave instead of being dropped.
    this.flushPendingSaves()
  }

  emitSave() {
    this.$emit('save', this.model)
  }

  flushTextSave() {
    this.queueTextSave.flush()
  }

  // flush() only invokes when a call is actually pending, so this never emits spurious saves.
  flushPendingSaves() {
    this.queueSave.flush()
    this.queueTextSave.flush()
  }

  get liveStatus(): string {
    return this.runState?.status ?? 'idle'
  }

  // Pending autosaves are cancelled — re-saving something being deleted makes no sense (and could
  // race the delete). The parent removes the real record and navigates back on success.
  onDeleteConfirm() {
    this.queueSave.cancel()
    this.queueTextSave.cancel()
    this.confirmDelete = false
    this.$emit('delete', this.model)
  }

  // Run switches straight to the Runs tab (the Advanced editors do the same) so progress is visible.
  // Pending autosaves are cancelled, not flushed: the run path saves the emitted model itself.
  onRunClick() {
    this.queueSave.cancel()
    this.queueTextSave.cancel()
    this.view = 'runs'
    this.$emit('run', this.model)
  }

  get meta() {
    return TYPE_META[this.model.type] ?? TYPE_META.schedule
  }

  get whenLabel(): string {
    return whenLine(this.model.when)
  }

  get inputsHint(): string {
    // Factory inputs aren't part of the saved config — they're handed to the run as its starting data.
    if (this.model.type === 'factory') return 'The starting data handed to the run when you press Run.'
    return 'The information it starts from. Assistants can be pointed at any of these.'
  }

  get howHint(): string {
    if (this.model.type === 'workflow')
      return 'Helpers gather what’s needed, then the lead assistant pulls it together. Tap any assistant or input to change it.'
    if (this.model.type === 'factory') return 'Steps run top to bottom. Each step can move on, jump back to repeat, or finish.'
    return 'One assistant runs this on the schedule below.'
  }

  agentOptions(current: string): ChipOption[] {
    return this.agents.includes(current) ? this.agents : [current, ...this.agents]
  }

  patch(p: Partial<Automation>) {
    this.model = { ...this.model, ...p }
    this.$emit('update:automation', this.model)
  }

  // Schedules don't autosave — they keep the explicit Save button, matching the Advanced
  // ScheduledRunCard. (A timer-fired run mid-edit makes uncommitted schedule state riskier.)
  get autosaves(): boolean {
    return this.model.type !== 'schedule'
  }

  // Discrete edit (chips, toggles, steps, when): autosave on the Advanced editors' 500ms debounce.
  patchDiscrete(p: Partial<Automation>) {
    this.patch(p)
    if (this.autosaves) this.queueSave()
  }

  // Structural chip intent (agent/input edit or removal) from WorkflowFlow: forwarded as an explicit
  // HelperTokenEdit alongside the current model. The parent rewrites exactly that prompt token and
  // hands the folded model back via the automation prop (the watcher re-syncs `model`), so the
  // queued autosave emits the folded version. Never inferred by diffing chip arrays.
  onHelperIntent(e: { index: number; from: { agent: string; input: string }; to?: { agent: string; input: string } }) {
    const edit: HelperTokenEdit = { from: e.from, to: e.to ?? null }
    this.$emit('update:automation', this.model, edit)
    if (this.autosaves) this.queueSave()
  }

  // Free-text edit (name, goal): the Advanced editors' slow lane — 5s of quiet, or blur.
  patchText(p: Partial<Automation>) {
    this.patch(p)
    if (this.autosaves) this.queueTextSave()
  }

  // The payload editor edits the raw dataPayload; re-derive the flat inputs so the workflow's
  // "to read" options reflect the current payload keys. Autosaved like the Advanced payload editor.
  onPayload(dataPayload: string) {
    this.patchDiscrete({ dataPayload, inputs: inputsFromPayload(dataPayload) })
  }

  toggleStatus() {
    this.patchDiscrete({ status: this.model.status === 'active' ? 'paused' : 'active' })
  }
}
</script>

<style scoped lang="scss">
.ms-detail {
  margin: 0 auto;

  // One width for every type (workflows need it for the visual data-payload tree; schedules and
  // factories match so the detail doesn't jump between sizes).
  &--wide {
    max-width: 1120px;
  }
}
</style>
